import { AutocompleteClasses, InputBaseComponentProps, SxProps, Theme } from "@mui/material";
import React, { useCallback } from "react";
import { ReactNode } from "react-markdown";

import { useDetectorGroups } from "api/services/useDetectorGroups";
import { Autocomplete, OptionsPage } from "components/common/Autocomplete/Autocomplete";
import { extractPaginateFrom } from "utils/utilityFunctions";

import {
  DETECTOR_GROUP_ALL_OPTION,
  DETECTOR_GROUP_CREATE_NEW_OPTION,
  DETECTOR_GROUP_DEFAULT_OPTION,
} from "./constants";
import { DetectorGroupOption } from "./types";

type DetectorGroupAutocompleteProps = {
  value?: DetectorGroupOption | null;
  onChange?: (option?: DetectorGroupOption) => void;
  classes?: Partial<AutocompleteClasses>;
  popupIcon?: ReactNode;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  inputProps?: InputBaseComponentProps;

  /*
   * If true, the first option is automatically highlighted.
   *
   * @default false
   */
  autoHighlight?: boolean;

  /**
   * @default Group
   */
  label?: string;

  /**
   * @default false
   */
  hasAllOption?: boolean;

  /**
   * @default false
   */
  hasCreateOption?: boolean;

  /**
   * @default false
   */
  autoSelectFirst?: boolean;

  /**
   * @default false
   */
  hasDefaultOption?: boolean;

  /**
   * @default false
   */
  showLoadingIndicator?: boolean;
};

export function DetectorGroupAutocomplete({
  value,
  onChange,
  hasAllOption = false,
  hasCreateOption = false,
  classes,
  popupIcon,
  sx,
  autoSelectFirst = false,
  hasDefaultOption = false,
  showLoadingIndicator = false,
  disabled,
  label = "Group",
  inputProps,
  autoHighlight = false,
}: DetectorGroupAutocompleteProps) {
  const { fetchDetectorGroups } = useDetectorGroups();

  const loadOptions = useCallback(
    async (searchText: string, page?: OptionsPage<string>) => {
      const next = page?.next ?? "";
      const paginateFrom = extractPaginateFrom(next);
      const response = await fetchDetectorGroups({
        page_size: 60,
        ordering: "name",
        paginate_from: paginateFrom || undefined,
        search: searchText,
      });

      const options = response.results.map((detectorGroup) => ({
        label: detectorGroup.name,
        value: detectorGroup.id,
      }));

      return {
        options,
        next: response.next,
        previous: response.previous,
      };
    },
    [fetchDetectorGroups]
  );

  return (
    <Autocomplete
      showLoadingIndicator={showLoadingIndicator}
      value={value}
      label={label}
      onChange={onChange}
      autoSelectFirst={autoSelectFirst}
      allOption={hasAllOption ? DETECTOR_GROUP_ALL_OPTION : undefined}
      createOption={hasCreateOption ? DETECTOR_GROUP_CREATE_NEW_OPTION : undefined}
      defaultOption={hasDefaultOption ? DETECTOR_GROUP_DEFAULT_OPTION : undefined}
      autoHighlight={autoHighlight}
      inputProps={inputProps}
      disabled={disabled}
      popupIcon={popupIcon}
      classes={classes}
      loadOptions={loadOptions}
      sx={sx}
    />
  );
}
